import { useEffect, useState } from 'react';
import { flotte } from '../api.js';
import type { DétailSiteRéponse } from '../api.js';
import { Sparkline } from '../components/Sparkline.js';
import { Configuration } from './Configuration.js';
import { MisesÀJour } from './MisesAJour.js';
import { Sauvegardes } from './Sauvegardes.js';

interface Props {
  organizationId: string;
  siteId: string;
  rôle: string;
  onRetour: () => void;
}

const LIBELLÉS_ALERTE: Record<string, string> = {
  site_down: 'Site en panne',
  disk_high: 'Disque presque plein',
  backup_failed: 'Sauvegarde en échec',
  update_failed: 'Mise à jour en échec',
  major_available: 'Version majeure disponible',
  ssh_host_key_mismatch: 'Empreinte SSH modifiée',
};

/** Vue détaillée d'un site : état, alertes ouvertes, et courbe de latence sur sept jours. */
export function DétailSite({ organizationId, siteId, rôle, onRetour }: Props) {
  const [données, setDonnées] = useState<DétailSiteRéponse | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  // Un seul panneau à la fois : le détail sert d'aiguillage vers les trois modules du site.
  const [panneau, setPanneau] = useState<'aucun' | 'config' | 'sauvegardes' | 'misesAJour'>(
    'aucun',
  );

  useEffect(() => {
    let annulé = false;
    void (async () => {
      try {
        const { status, data } = await flotte.détail(organizationId, siteId);
        if (annulé) return;
        if (status >= 400) {
          setErreur(data.error ?? 'Chargement impossible');
          return;
        }
        setDonnées(data);
      } catch {
        if (!annulé) setErreur('Service injoignable');
      }
    })();
    return () => {
      annulé = true;
    };
  }, [organizationId, siteId]);

  if (erreur !== null) return <p className="erreur">{erreur}</p>;
  if (données === null) return <p className="aide">Chargement…</p>;

  const { site } = données;

  if (panneau !== 'aucun') {
    const commun = {
      organizationId,
      siteId,
      siteNom: site.name,
      rôle,
      onRetour: () => setPanneau('aucun'),
    };
    if (panneau === 'config') {
      return (
        <Configuration
          {...commun}
          modeRedémarrage={site.restartMode}
          consigneRedémarrage={site.restartHint}
        />
      );
    }
    if (panneau === 'sauvegardes') return <Sauvegardes {...commun} />;
    return (
      <MisesÀJour
        {...commun}
        consigneRedémarrage={site.restartMode === 'external' ? site.restartHint : null}
      />
    );
  }

  return (
    <section className="carte">
      <button type="button" className="lien" onClick={onRetour}>
        ← Retour à la flotte
      </button>

      <h2>{site.name}</h2>
      <p className="aide">
        <a href={site.url} target="_blank" rel="noreferrer noopener">
          {site.url}
        </a>
      </p>

      {données.alerts.length > 0 && (
        <>
          <h3>Alertes ouvertes</h3>
          {données.alerts.map((alerte) => (
            <p key={alerte.id} className="erreur">
              {LIBELLÉS_ALERTE[alerte.type] ?? alerte.type} — depuis le{' '}
              {new Date(alerte.openedAt).toLocaleString('fr-FR')}
            </p>
          ))}
        </>
      )}

      <div className="actions">
        <button type="button" onClick={() => setPanneau('config')}>
          Configuration
        </button>
        <button type="button" onClick={() => setPanneau('sauvegardes')}>
          Sauvegardes
        </button>
        <button type="button" onClick={() => setPanneau('misesAJour')}>
          Mises à jour
        </button>
      </div>

      <h3>Latence sur 7 jours</h3>
      <Sparkline points={données.history} />

      <h3>Fiche technique</h3>
      <dl className="fiche">
        <div>
          <dt>Type d’installation</dt>
          <dd>{site.installType}</dd>
        </div>
        <div>
          <dt>Version de Ghost</dt>
          <dd>{site.ghostVersionDetected ?? 'inconnue'}</dd>
        </div>
        <div>
          <dt>Version de Node</dt>
          <dd>{site.nodeVersionDetected ?? 'inconnue'}</dd>
        </div>
        <div>
          <dt>Sauvegardes</dt>
          <dd>
            {site.backupMethod} · {site.backupSchedule}
          </dd>
        </div>
        <div>
          <dt>Mises à jour</dt>
          <dd>{site.updatePolicy}</dd>
          <dt>Redémarrage</dt>
          <dd>
            {site.restartMode === 'external'
              ? 'hors de Vigie, dans le panneau de l’hébergeur'
              : 'par Vigie'}
          </dd>
        </div>
        <div>
          <dt>Sonde</dt>
          <dd>toutes les {site.monitorIntervalMinutes} min</dd>
        </div>
        <div>
          <dt>Accès SSH</dt>
          <dd>
            {site.sshUser}@{site.sshHost}
          </dd>
        </div>
        <div>
          <dt>Empreinte SSH</dt>
          <dd className="empreinte">{site.sshHostKeyFingerprint ?? '—'}</dd>
        </div>
      </dl>
    </section>
  );
}
